<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import logo from '../../assets/home/logo.webp';
import { useCartStore } from '../../stores/cart';
import { useAuthStore } from '../../stores/auth';
import LanguageSwitcher from '../ui/LanguageSwitcher.vue';

const cart = useCartStore();
const auth = useAuthStore();
const { t } = useI18n();

const navLinks = computed<{ label: string; to?: string; href?: string }[]>(() => [
  { label: t('nav.catalog'), to: '/catalog' },
  { label: t('nav.articles'), to: '/articles' },
  { label: t('nav.faq'), to: '/faq' },
]);

const open = ref(false);
</script>

<template>
  <header class="relative z-30">
    <div class="relative mx-auto flex h-20 max-w-[1200px] items-center justify-between px-5">
      <!-- Logo -->
      <router-link to="/" class="flex shrink-0 items-center">
        <img :src="logo" alt="PlayStock" class="h-9 w-auto sm:h-12" />
      </router-link>

      <!-- Desktop nav (centered on the page, aligned with the orange curtain) -->
      <nav class="absolute left-1/2 hidden -translate-x-1/2 items-center gap-9 md:flex">
        <component
          :is="link.to ? 'router-link' : 'a'"
          v-for="link in navLinks"
          :key="link.label"
          :to="link.to"
          :href="link.href"
          class="text-base font-medium text-white transition hover:text-accent"
        >{{ link.label }}</component>
      </nav>

      <!-- Right cluster (desktop) -->
      <div class="hidden items-center gap-4 md:flex">
        <LanguageSwitcher align="right" />
        <router-link to="/favorites" class="text-white transition hover:text-accent" :aria-label="t('common.favorites')">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20.8 5.6a5.5 5.5 0 0 0-7.8 0L12 6.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 22l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" /></svg>
        </router-link>
        <router-link to="/cart" class="relative text-white transition hover:text-accent" :aria-label="t('common.cart')">
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="9" cy="20" r="1.4" /><circle cx="18" cy="20" r="1.4" /><path d="M2 3h2.2l2.2 12.2a2 2 0 0 0 2 1.6h8.5a2 2 0 0 0 2-1.6L21 7H5.5" /></svg>
          <span v-if="cart.count" class="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-white">{{ cart.count }}</span>
        </router-link>

        <template v-if="auth.isAuthenticated">
          <router-link to="/dashboard" class="max-w-[160px] truncate text-base font-medium text-white transition hover:text-accent">{{ auth.displayName }}</router-link>
          <button class="rounded-full border border-line px-4 py-2 text-sm font-medium text-white transition hover:border-accent hover:text-accent" @click="auth.logout()">{{ t('common.logOut') }}</button>
        </template>
        <template v-else>
          <router-link to="/login" class="text-base font-medium text-white transition hover:text-accent">{{ t('common.logIn') }}</router-link>
          <router-link to="/register" class="rounded-full bg-accent px-5 py-2 text-sm font-semibold text-white transition hover:bg-accent-2">{{ t('common.signUp') }}</router-link>
        </template>
      </div>

      <!-- Burger (mobile) -->
      <button
        class="relative grid h-10 w-10 place-items-center rounded-lg border border-line text-white transition hover:border-accent md:hidden"
        aria-label="Menu"
        @click="open = !open"
        type="button"
      >
        <svg v-if="!open" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M4 6h16M4 12h16M4 18h16" /></svg>
        <svg v-else width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"><path d="M6 6l12 12M18 6 6 18" /></svg>
        <span v-if="cart.count" class="absolute -right-1.5 -top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-accent px-1 text-[10px] font-bold leading-none text-white">{{ cart.count }}</span>
      </button>
    </div>

    <!-- Mobile menu -->
    <nav v-if="open" class="border-t border-line bg-bg/95 px-5 py-4 backdrop-blur md:hidden">
      <component
        :is="link.to ? 'router-link' : 'a'"
        v-for="link in navLinks"
        :key="link.label"
        :to="link.to"
        :href="link.href"
        class="block py-2.5 font-display text-sm uppercase tracking-wide text-white transition hover:text-accent"
        @click="open = false"
      >{{ link.label }}</component>

      <div class="my-3 border-t border-line"></div>
      <router-link to="/favorites" class="block py-2.5 text-sm text-muted transition hover:text-accent" @click="open = false">{{ t('common.favorites') }}</router-link>
      <router-link to="/cart" class="block py-2.5 text-sm text-muted transition hover:text-accent" @click="open = false">{{ t('common.cart') }} <span v-if="cart.count" class="text-accent">({{ cart.count }})</span></router-link>

      <div class="my-3 border-t border-line"></div>
      <template v-if="auth.isAuthenticated">
        <router-link to="/dashboard" class="block py-2.5 text-sm text-muted transition hover:text-accent" @click="open = false">{{ t('common.account') }}</router-link>
        <router-link to="/orders" class="block py-2.5 text-sm text-muted transition hover:text-accent" @click="open = false">{{ t('common.myOrders') }}</router-link>
        <button class="mt-2 w-full rounded-full border border-line py-2.5 text-sm font-medium text-white transition hover:border-accent" @click="auth.logout(); open = false">{{ t('common.logOut') }}</button>
      </template>
      <div v-else class="mt-2 flex gap-3">
        <router-link to="/login" class="flex-1 rounded-full border border-line py-2.5 text-center text-sm font-medium text-white transition hover:border-accent" @click="open = false">{{ t('common.logIn') }}</router-link>
        <router-link to="/register" class="flex-1 rounded-full bg-accent py-2.5 text-center text-sm font-semibold text-white transition hover:bg-accent-2" @click="open = false">{{ t('common.signUp') }}</router-link>
      </div>

      <div class="my-3 border-t border-line"></div>
      <div class="flex items-center justify-between py-1 text-sm font-medium text-white">
        <span class="text-muted">{{ t('footer.language') }}</span>
        <LanguageSwitcher align="right" />
      </div>
    </nav>
  </header>
</template>
